import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Agent } from '../types';

// Tipos de acciones para las sagas
export interface CreateAgentRequest {
  agentData: any;
}

export interface UpdateAgentRequest {
  id: string;
  agentData: any;
}

export interface GetAgentsRequest {
  filters?: any;
}

export interface GetAgentByIdRequest {
  id: string;
}

export interface DeleteAgentRequest {
  id: string;
}

export interface UpdateAgentStatusRequest {
  id: string;
  status: string;
}

// Estado inicial
export interface AgentState {
  agents: Agent[];
  currentAgent: Agent | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
}

const initialState: AgentState = {
  agents: [],
  currentAgent: null,
  loading: false,
  error: null,
  pagination: null,
};

const agentSlice = createSlice({
  name: 'agent',
  initialState,
  reducers: {
    // Acciones que disparan sagas
    createAgentRequest: (state, _action: PayloadAction<CreateAgentRequest>) => {
      state.loading = true;
      state.error = null;
    },
    updateAgentRequest: (state, _action: PayloadAction<UpdateAgentRequest>) => {
      state.loading = true;
      state.error = null;
    },
    getAgentsRequest: (state, _action: PayloadAction<GetAgentsRequest>) => {
      state.loading = true;
      state.error = null;
    },
    getAgentByIdRequest: (state, _action: PayloadAction<GetAgentByIdRequest>) => {
      state.loading = true;
      state.error = null;
    },
    deleteAgentRequest: (state, _action: PayloadAction<DeleteAgentRequest>) => {
      state.loading = true;
      state.error = null;
    },
    updateAgentStatusRequest: (state, _action: PayloadAction<UpdateAgentStatusRequest>) => {
      state.loading = true;
      state.error = null;
    },

    // Acciones que actualizan el estado (llamadas desde sagas)
    createAgentSuccess: (state, action: PayloadAction<Agent>) => {
      state.loading = false;
      state.agents.unshift(action.payload);
      state.error = null;
    },
    updateAgentSuccess: (state, action: PayloadAction<Agent>) => {
      state.loading = false;
      const index = state.agents.findIndex(agent => agent.id === action.payload.id);
      if (index !== -1) {
        state.agents[index] = action.payload;
      }
      if (state.currentAgent?.id === action.payload.id) {
        state.currentAgent = action.payload;
      }
      state.error = null;
    },
    getAgentsSuccess: (state, action: PayloadAction<{
      agents: Agent[];
      pagination?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>) => {
      state.loading = false;
      state.agents = action.payload.agents;
      state.pagination = action.payload.pagination || null;
      state.error = null;
    },
    getAgentByIdSuccess: (state, action: PayloadAction<Agent>) => {
      state.loading = false;
      state.currentAgent = action.payload;
      state.error = null;
    },
    deleteAgentSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.agents = state.agents.filter(agent => agent.id !== action.payload);
      if (state.currentAgent?.id === action.payload) {
        state.currentAgent = null;
      }
      state.error = null;
    },
    updateAgentStatusSuccess: (state, action: PayloadAction<Agent>) => {
      state.loading = false;
      const index = state.agents.findIndex(agent => agent.id === action.payload.id);
      if (index !== -1) {
        state.agents[index] = action.payload;
      }
      if (state.currentAgent?.id === action.payload.id) {
        state.currentAgent = action.payload;
      }
      state.error = null;
    },
    agentFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearAgentError: (state) => {
      state.error = null;
    },
    clearCurrentAgent: (state) => {
      state.currentAgent = null;
    },
    clearAgents: (state) => {
      state.agents = [];
      state.currentAgent = null;
      state.pagination = null;
      state.error = null;
    },
  },
});

export const {
  createAgentRequest,
  updateAgentRequest,
  getAgentsRequest,
  getAgentByIdRequest,
  deleteAgentRequest,
  updateAgentStatusRequest,
  createAgentSuccess,
  updateAgentSuccess,
  getAgentsSuccess,
  getAgentByIdSuccess,
  deleteAgentSuccess,
  updateAgentStatusSuccess,
  agentFailure,
  clearAgentError,
  clearCurrentAgent,
  clearAgents,
} = agentSlice.actions;

export default agentSlice.reducer;
